import { classNames } from 'primereact/utils';
import { useEffect, useRef, useState } from 'react';

import { IconShowMore } from '@/components/icons';

type Props = {
  isRead: boolean;
  setIsRead: (isRead: boolean) => void;
};

const RegisterAccountPolicy = ({ isRead, setIsRead }: Props) => {
  const [isShowMore, setIsShowMore] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  const handleShowMore = () => {
    setIsShowMore((prev) => !prev);
  };

  const handleScroll = () => {
    if (!contentRef.current || isRead) return;

    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setIsRead(true);
    }
  };

  useEffect(() => {
    const contentEl = contentRef.current;
    if (contentEl) {
      contentEl.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (contentEl) {
        contentEl.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isRead]);

  return (
    <div className="text-[#344054] text-sm mt-[6px]">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div
          className={classNames('transition-all', {
            'max-h-[250px] overflow-hidden overflow-y-scroll': !isShowMore,
            'max-h-[500px] overflow-hidden overflow-y-scroll': isShowMore,
          })}
          ref={contentRef}
        >
          <p className="leading-snug">
            一般社団法人アート・シフト・コーチング協会（以下「当協会」といいます。）は、当協会が提供するサービス（以下「本サービス」といいます。）における利用者についての個人情報の取扱いについて、以下のとおりプライバシーポリシー（以下「本ポリシー」といいます。）を定めます。
          </p>
          <p className="leading-snug mt-2">
            なお、本文中の用語の定義は、個人情報の保護に関する法律（平成15年法律第57号。以下「個人情報保護法」といいます。）および関連法令に従います。
          </p>

          {/* Section 1 */}
          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">1. 個人情報の取得・利用</h3>
            <p className="leading-snug mt-1">
              当協会は、以下の目的に必要な範囲で、利用者の個⼈情報を取得し、取得した情報を利用します。以下の⽬的の範囲を超えて個⼈情報を利⽤する場合には、事前に適切な⽅法で利用者からの同意を得ます。
            </p>
            {[
              '本サービスを提供するため',
              '本サービスの内容を改良・改善し、または新サービスを開発するため',
              '本サービスの新機能、更新情報、キャンペーン等および当協会が提供する他のサービスのご案内（電子メール、チラシ、その他のダイレクトメールの送付を含む。）のため',
              '重要なお知らせ、アンケート、お問合せへの対応など必要に応じたご連絡のため',
              '本サービスの利用履歴等を調査・分析し、その結果を本サービスの改良・開発や広告の配信に利用するため',
              '取得した閲覧履歴や購買履歴等の情報を分析して、趣味・嗜好に応じた新商品・サービスに関する広告の配信に利用するため',
              '利用規約に違反した利用者や不正・不当な目的で本サービスを利用しようとする利用者の特定への対応のため',
              'その他前各号に付随する業務のため',
            ].map((item, index) => (
              <p key={index} className="ml-2 mt-1">
                {index + 1}. {item}
              </p>
            ))}
          </section>

          {/* Section 2 */}
          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">2. 個人情報の管理・保護</h3>
            <p className="leading-snug mt-1">
              当協会は、取得した個人情報を厳重に管理し、以下の場合を除き、利用者の同意がない限り、第三者に対し個人情報を開示・提供することはいたしません。また、当協会は、その取り扱う個人情報の漏えい、滅失または毀損の防止その他個人情報の安全管理のために、必要かつ適切な措置を講じます。
            </p>
            {[
              '利用目的の達成に必要な範囲内において、個人情報の取扱いの全部または一部を第三者に委託する場合',
              '合併その他の事由による事業の承継に伴って個人情報が提供される場合',
              '国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、利用者の同意を得ることによって当該事務の遂行に支障を及ぼすおそれがある場合',
              'その他、個人情報保護法その他の法令で認められる場合',
            ].map((item, index) => (
              <p key={index} className="ml-2 mt-1">
                {index + 1}. {item}
              </p>
            ))}
          </section>

          {/* Section 3 */}
          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              3. 個人情報の取扱いの委託
            </h3>
            <p className="leading-snug mt-1">
              当協会は、利用目的の達成に必要な範囲内において、個人情報の取扱いの全部または一部を委託する場合があります。この場合、当協会は、委託先としての適格性を十分審査するとともに、契約にあたって守秘義務に関する事項等を定め、委託先に対する必要かつ適切な監督を行います。
            </p>
          </section>

          {/* Section 4 */}
          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">4. 個人情報の開示</h3>
            <p className="leading-snug mt-1">
              当協会は、利用者から個人情報または個人情報の第三者提供に関する記録の開示を求められたときは、利用者に対し、遅滞なくこれを開示します。ただし、開示することにより次のいずれかに該当する場合は、その全部または一部を開示しないことがあり、開示しない決定をした場合には、その旨を遅滞なく通知します。なお、開示請求の手続き費用として、１件あたり１，０００円をお支払いいただきます。手続き費用の支払方法は、定額小為替による納付とします。
            </p>
            {[
              '利用者または第三者の生命、身体、財産その他の権利利益を害するおそれがある場合',
              '当協会の業務の適正な実施に著しい支障を及ぼすおそれがある場合',
              'その他法令に違反することとなる場合',
            ].map((item, index) => (
              <p key={index} className="ml-2 mt-1">
                {index + 1}. {item}
              </p>
            ))}
          </section>

          {/* Section 5 */}
          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">5. 個人情報の訂正・削除</h3>
            <p className="leading-snug mt-1">
              当協会の保有する個人情報が誤った情報である場合には、利用者からの請求により、当協会が定める手続に従い個人情報の訂正または削除を行います。当協会は、利用者からの請求に応じる必要があると判断して当該個人情報の訂正または削除を行った場合には、遅滞なくその旨を利用者に通知します。
            </p>
          </section>

          {/* Section 6 */}
          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">6. 個人情報の利用停止等</h3>
            <p className="leading-snug mt-1">
              当協会は、利用者から、個人情報保護法に定める事由により、その利用の停止または消去（第三者提供の停止を含み、以下「利用停止等」といいます。）を求められた場合には、遅滞なく必要な調査を行い、請求に理由があると認められる場合は、必要な範囲で、個人情報の利用停止等を行い、その旨を利用者に通知します。ただし、個人情報の利用停止等に多額の費用を有する場合その他利用停止等を行うことが困難な場合であって、利用者の権利利益を保護するために必要な代替措置をとることができる場合は、この代替措置を講じます。
            </p>
          </section>

          {/* Section 7 */}
          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">7. Cookie等の取扱い</h3>
            <p className="leading-snug mt-1">
              当協会は、利用者のログイン情報等を適切に管理し、利用者がよりご満足いただけるようなサービスの改良を行うため、Cookieを利用しております。また、当協会は、利用者の本サービスへの訪問状況等を把握し、本サービスの改良のために、Google社のサービスであるGoogle
              Analyticsを利用しています。なお、ブラウザのアドオン設定でGoogle
              Analyticsを無効にすることにより、当協会のGoogle
              Analytics利用による利用者の情報の収集を停止することも可能です。
              Google アナリティクス オプトアウト アドオン：
              <a
                href="https://tools.google.com/dlpage/gaoptout?hl=ja"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-500 underline"
              >
                https://tools.google.com/dlpage/gaoptout?hl=ja
              </a>
            </p>
          </section>

          {/* Section 8 */}
          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              8. プライバシーポリシーの変更手続
            </h3>
            <p className="leading-snug mt-1">
              当協会は、必要に応じて、本ポリシーを変更することができます。ただし、法令上利用者の同意が必要となるような本ポリシーの変更を行う場合、変更後の本ポリシーは、当協会所定の方法で変更に同意した利用者に対してのみ適用されるものとします。なお、当協会は、本ポリシーを変更する場合には、変更後の本ポリシーの施行時期および内容を当協会のウェブサイト上での表示その他の適切な方法により周知し、または利用者に通知します。
            </p>
          </section>

          {/* Section 9 */}
          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              9. 法令・ガイドライン等の遵守
            </h3>
            <p className="leading-snug mt-1">
              利用者の個人情報の取扱に関する事項で本ポリシーに記載のないものについては、個人情報保護法その他の法令・ガイドライン等の定めに従います。
            </p>
          </section>

          {/* Section 10 */}
          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              10. 他の規約等との適用関係
            </h3>
            <p className="leading-snug mt-1">
              本ポリシーの内容と本サービスに関する当協会の利用規約等（利用者が同意したものに限ります。）の内容に齟齬がある場合は、本サービスに関する当協会の利用規約等が本ポリシーに優先して適用されるものとします。
            </p>
          </section>

          {/* Section 11 */}
          <section className="mt-6 ">
            <h3 className="text-lg font-bold mb-2">11. お問い合わせ</h3>
            <p className="leading-snug mt-1">
              本ポリシーに関するお問い合わせは、以下の窓口までご連絡ください。
            </p>
            <p className="ml-2 mt-1">
              一般社団法人アート・シフト・コーチング協会
            </p>
            <p className="ml-2 mt-1">
              〒150-0001 東京都渋谷区神宮前5-51-6
            </p>
            <p className="ml-2 mt-1">電話番号：03-6899-1750</p>
          </section>
        </div>

        <div
          className="border-t border-gray-200 pt-4 justify-center items-center mt-2 text-primary-500 flex hover:cursor-pointer"
          onClick={handleShowMore}
        >
          <div>{isShowMore ? '表示を減らす' : 'もっと見る'}</div>
          <span
            className={classNames({ 'rotate-180 transition-all': isShowMore })}
          >
            <IconShowMore />
          </span>
        </div>
      </div>
    </div>
  );
};

export default RegisterAccountPolicy;
